// WHICH logline and WHICH plot an act, an episode or a scene shows — the one
// place that answers it (#1841 item 09). The rule: the active atom is the one
// that is not a draft. A draft is a key in the atom's own `content_json`
// (`data/textAtomDraft.ts`), which is why this lives here and not in a screen.
//
// **An entity can have several atoms of one type, and at most one of them is
// active** (#1841 item 11). Every atom of the type being a draft is a legal
// state, and then the entity simply has no logline (or no plot): the plan, the
// Navigator, the cards and the export show nothing for it, exactly as they do
// for an entity that never had one. Only the editor sheet still draws those
// rows — it renders the whole list rather than asking here
// (`ActEpisodeRows`/`hostedTextRows`), which is what keeps a set-aside logline
// reachable while nothing shows it as the entity's own.

import { type ContentJsonColumn, readTextAtomDraft } from '@/data/textAtomDraft';
import { textAtomTypeId } from '@/enums';
import type { IFountainAct, IFountainFragment, IFountainScene, TextAtomContentJson } from '@/types';

/** Structural, so the devtools filler's narrower wire shape can ask too. */
interface AtomLike {
    text_atom_type_id: number;
    content_json?: ContentJsonColumn;
}

export type EntityType = 'act' | 'fragment' | 'scene';
type TextField = 'logline' | 'plot';

/** The wire names are not parallel — hence a table rather than a remembered
 *  asymmetry in every screen. */
const TEXT_FIELD_ATOM_TYPE: Record<EntityType, Record<TextField, textAtomTypeId>> = {
    act: { logline: textAtomTypeId.act_logline, plot: textAtomTypeId.act_plot },
    fragment: { logline: textAtomTypeId.fragment_logline, plot: textAtomTypeId.episode_plot },
    scene: { logline: textAtomTypeId.scene_logline, plot: textAtomTypeId.scene_synopsis },
};

/** The first atom of the type that is not a draft, whatever order the backend
 *  sent — and `undefined` when every one of them is, which is an entity with
 *  nothing active rather than an entity showing a draft as if it were.
 *
 *  No short-circuit on a lone atom: that one IS the case the rule now decides
 *  (a single set-aside logline leaves the entity without one), and reading the
 *  flag costs a substring test on the atoms nobody has drafted
 *  ({@link readTextAtomDraft}). */
export function findActiveTextAtom<T extends AtomLike>(atoms: readonly T[] | undefined, typeId: textAtomTypeId): T | undefined {
    return atoms?.find(atom => atom.text_atom_type_id === typeId && !readTextAtomDraft(atom.content_json).drafted);
}

/** The atoms-in form. Inside a `useMemo` prefer it to the entity-in getters
 *  below (`getActLoglineAtom(act)` …): those force `[act]` into the deps, where
 *  this keeps the narrow `[act.text_atoms]` key the rows and headers memo on. */
export function getActiveTextAtom<T extends AtomLike>(entityType: EntityType, field: TextField, atoms: readonly T[] | undefined) {
    return findActiveTextAtom(atoms, TEXT_FIELD_ATOM_TYPE[entityType][field]);
}

/** Which atom type an entity's logline / plot is — the table above, for the one
 *  caller that wants every atom of the type rather than the active one
 *  (`hostedTextRows`). */
export function textFieldAtomType(entityType: EntityType, field: TextField): textAtomTypeId {
    return TEXT_FIELD_ATOM_TYPE[entityType][field];
}

// ============================================================================
// Unified accessor — what `saveText` addresses its write by
// ============================================================================

export function getTextAtomId(
    entityType: EntityType,
    entity: IFountainAct | IFountainFragment | IFountainScene,
    field: TextField,
): number | undefined {
    return getActiveTextAtom(entityType, field, entity.text_atoms)?.id;
}

// ============================================================================
// Act accessors
// ============================================================================

export const getActLoglineAtom = (act: IFountainAct) => getActiveTextAtom('act', 'logline', act.text_atoms);

export const getActPlotAtom = (act: IFountainAct) => getActiveTextAtom('act', 'plot', act.text_atoms);

export const getActLogline = (act: IFountainAct): string => getActLoglineAtom(act)?.content || '';

export const getActLoglineAtomId = (act: IFountainAct): number | undefined => getActLoglineAtom(act)?.id;

export const getActPlot = (act: IFountainAct): string => getActPlotAtom(act)?.content || '';

export const getActPlotAtomId = (act: IFountainAct): number | undefined => getActPlotAtom(act)?.id;

export const getActLoglineContentJson = (act: IFountainAct): TextAtomContentJson | undefined => getActLoglineAtom(act)?.content_json;

export const getActPlotContentJson = (act: IFountainAct): TextAtomContentJson | undefined => getActPlotAtom(act)?.content_json;

// ============================================================================
// Episode (Fragment) accessors
// ============================================================================

export const getEpisodeLoglineAtom = (fragment: IFountainFragment) => getActiveTextAtom('fragment', 'logline', fragment.text_atoms);

export const getEpisodePlotAtom = (fragment: IFountainFragment) => getActiveTextAtom('fragment', 'plot', fragment.text_atoms);

export const getEpisodeLogline = (fragment: IFountainFragment): string => getEpisodeLoglineAtom(fragment)?.content || '';

export const getEpisodeLoglineAtomId = (fragment: IFountainFragment): number | undefined => getEpisodeLoglineAtom(fragment)?.id;

export const getEpisodePlot = (fragment: IFountainFragment): string => getEpisodePlotAtom(fragment)?.content || '';

export const getEpisodePlotAtomId = (fragment: IFountainFragment): number | undefined => getEpisodePlotAtom(fragment)?.id;

export const getEpisodeLoglineContentJson = (fragment: IFountainFragment): TextAtomContentJson | undefined =>
    getEpisodeLoglineAtom(fragment)?.content_json;

export const getEpisodePlotContentJson = (fragment: IFountainFragment): TextAtomContentJson | undefined =>
    getEpisodePlotAtom(fragment)?.content_json;

// ============================================================================
// Scene accessors
// ============================================================================

export const getSceneLoglineAtom = (scene: IFountainScene) => getActiveTextAtom('scene', 'logline', scene.text_atoms);

export const getScenePlotAtom = (scene: IFountainScene) => getActiveTextAtom('scene', 'plot', scene.text_atoms);

export const getSceneLogline = (scene: IFountainScene): string => getSceneLoglineAtom(scene)?.content || '';

export const getSceneLoglineAtomId = (scene: IFountainScene): number | undefined => getSceneLoglineAtom(scene)?.id;

export const getScenePlot = (scene: IFountainScene): string => getScenePlotAtom(scene)?.content || '';

export const getScenePlotAtomId = (scene: IFountainScene): number | undefined => getScenePlotAtom(scene)?.id;

export const getSceneLoglineContentJson = (scene: IFountainScene): TextAtomContentJson | undefined =>
    getSceneLoglineAtom(scene)?.content_json;

export const getScenePlotContentJson = (scene: IFountainScene): TextAtomContentJson | undefined => getScenePlotAtom(scene)?.content_json;
